import { createApp } from 'vue';
import { createPinia } from 'pinia';
import VueClickAway from 'vue3-click-away';
import App from '@/App.vue';
import router from '@/router';
import events from '@/router/events';
import i18n from '@/i18n';
import { createLx } from '@dativa-lv/lx-ui';
import { APP_CONFIG, AUTH_KEY_TOKEN_SESSION, SYSTEM_NAME } from '@/constants';

import '@dativa-lv/lx-ui/dist/styles/lx-reset.css';
import '@dativa-lv/lx-ui/dist/styles/lx-fonts-carbon.css';
import '@dativa-lv/lx-ui/dist/styles/lx-pt-carbon.css';
import '@dativa-lv/lx-ui/dist/styles/lx-ut-carbon-light.css';
import '@dativa-lv/lx-ui/dist/styles/lx-ut-carbon-dark.css';
import '@dativa-lv/lx-ui/dist/styles/lx-ut-carbon-contrast.css';

import '@dativa-lv/lx-ui/dist/styles/lx-buttons.css';
import '@dativa-lv/lx-ui/dist/styles/lx-data-grid.css';
import '@dativa-lv/lx-ui/dist/styles/lx-inputs.css';
import '@dativa-lv/lx-ui/dist/styles/lx-steps.css';
import '@dativa-lv/lx-ui/dist/styles/lx-forms.css';
import '@dativa-lv/lx-ui/dist/styles/lx-notifications.css';
import '@dativa-lv/lx-ui/dist/styles/lx-modal.css';
import '@dativa-lv/lx-ui/dist/styles/lx-loaders.css';
import '@dativa-lv/lx-ui/dist/styles/lx-lists.css';
import '@dativa-lv/lx-ui/dist/styles/lx-expanders.css';
import '@dativa-lv/lx-ui/dist/styles/lx-tabs.css';
import '@dativa-lv/lx-ui/dist/styles/lx-animations.css';
import '@dativa-lv/lx-ui/dist/styles/lx-master-detail.css';
import '@dativa-lv/lx-ui/dist/styles/lx-ratings.css';
import '@dativa-lv/lx-ui/dist/styles/lx-day-input.css';
import '@dativa-lv/lx-ui/dist/styles/lx-map.css';
import '@dativa-lv/lx-ui/dist/styles/lx-shell-grid.css';
import '@dativa-lv/lx-ui/dist/styles/lx-shell-grid-public.css';
import '@dativa-lv/lx-ui/dist/styles/lx-forms-grid.css';
import '@dativa-lv/lx-ui/dist/styles/lx-treelist.css';
import '@dativa-lv/lx-ui/dist/styles/lx-date-pickers.css';
import '@dativa-lv/lx-ui/dist/styles/lx-data-visualizer.css';
import '@dativa-lv/lx-ui/dist/styles/lx-stack.css';
import '@dativa-lv/lx-ui/dist/styles/lx-cards.css';
import '@dativa-lv/lx-ui/dist/styles/lx-transparency.css';
// Required as separate stylesheets since lx-ui 2.1.x (see MIGRATION.md):
import '@dativa-lv/lx-ui/dist/styles/lx-toggles.css';
import '@dativa-lv/lx-ui/dist/styles/lx-content-switchers.css';
import '@dativa-lv/lx-ui/dist/styles/lx-badges.css';

import '@/assets/styles.css';
import '@/assets/lx-pt-demo.css';
import { clerkPlugin, useAuth } from '@clerk/vue';
import { setTokenGetter } from '@/api';

const PUBLISHABLE_KEY = window.config.clerkPublicKey;

if (!PUBLISHABLE_KEY) {
  throw new Error('Add your Clerk Publishable Key to the .env file');
}

const myApp = createApp(App);
myApp.use(createPinia());

// Install Clerk plugin BEFORE setting up router events
// This is required because router/events.js uses useAuth()
myApp.use(clerkPlugin, {
  publishableKey: PUBLISHABLE_KEY,
});

myApp.mixin({
  setup() {
    const { getToken } = useAuth();
    setTokenGetter(() => (typeof getToken.value === 'function' ? getToken.value() : null));
  },
});

events(router);
myApp.use(router);

myApp.use(i18n);
myApp.use(VueClickAway);
myApp.use(createLx, {
  systemId: SYSTEM_NAME,
  authSessionKey: AUTH_KEY_TOKEN_SESSION,
  authUrl: APP_CONFIG.authUrl,
  authClientId: APP_CONFIG.clientId,
  publicUrl: APP_CONFIG.publicUrl,
  environment: APP_CONFIG.environment,
});
myApp.mount('#app');
